import { defineStore } from 'pinia'
import { ref, onMounted, computed } from 'vue'
import {
  type SuperUserQuestionnaire as Questionnaire,
  type GenericDataMapper,
  QuestionnairesService,
  OrganisationsService,
  LawFieldsService,
  type PaginatedTOrganisationList as OrganisationsList,
  type LawFields,
  CategoryEnum,
  FieldTypeEnum,
  type QuestionnaireQuestion,
  type QuestionnaireSection,
  CasesService,
  TagRelationshipEnum,
  RelationshipTypeEnum
} from '@/types/generated'
import { type Option } from '@/components/admin/form/SelectField.vue'
import {
  EnvelopeIcon,
  HashtagIcon,
  CalendarIcon,
  LinkIcon,
  PhoneIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  QuestionMarkCircleIcon,
  ListBulletIcon,
  PencilSquareIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import { v4 as uuidv4 } from 'uuid'
import { TimerIcon } from 'lucide-vue-next'
import { entitiesDE } from '@/i18n/entity'
import { lawFieldsDE, lawFieldsEN, lawFieldsFR } from '@/i18n/lawFields'
import { lawTopicsFR } from '@/i18n/lawTopics'
import { useLocaleStore } from '@/store/localStore'

export const useQuestionnaireEditorStore = defineStore('questionnaireEditor', () => {
  const localeStore = useLocaleStore()
  const questionnaire = ref<Questionnaire | null>(null)

  const tags = ref<GenericDataMapper[]>([])
  const tagOptions = computed(() => {
    return tags.value.map((tag) => ({ value: tag.key, title: tag.label }))
  })

  const fieldTypes = Object.values(FieldTypeEnum).map((type) => ({ key: type }))
  const fieldTypeIcons: { [key in FieldTypeEnum]: any } = {
    [FieldTypeEnum.MULTISELECT]: ListBulletIcon,
    [FieldTypeEnum.SELECT]: CheckCircleIcon,
    [FieldTypeEnum.TEXTAREA]: DocumentTextIcon,
    [FieldTypeEnum.FILEFIELD]: ArrowUpTrayIcon,
    [FieldTypeEnum.MULTIFILEFIELD]: ArrowUpTrayIcon,
    [FieldTypeEnum.TIMEFIELD]: TimerIcon,
    [FieldTypeEnum.DATEFIELD]: CalendarIcon,
    [FieldTypeEnum.TEXTINPUT]: PencilSquareIcon,
    [FieldTypeEnum.NUMBERINPUT]: HashtagIcon,
    [FieldTypeEnum.DECIMALNUMBERINPUT]: HashtagIcon,
    [FieldTypeEnum.EMAILINPUT]: EnvelopeIcon,
    [FieldTypeEnum.PHONEINPUT]: PhoneIcon,
    [FieldTypeEnum.URLINPUT]: LinkIcon,
    [FieldTypeEnum.YESNO]: QuestionMarkCircleIcon
  }

  const fieldTypeLabels: { [key in FieldTypeEnum]: string } = {
    [FieldTypeEnum.MULTISELECT]: 'Multiple choice',
    [FieldTypeEnum.SELECT]: 'Single choice',
    [FieldTypeEnum.TEXTAREA]: 'Text area',
    [FieldTypeEnum.FILEFIELD]: 'Single file upload',
    [FieldTypeEnum.MULTIFILEFIELD]: 'Multiple file upload',
    [FieldTypeEnum.TIMEFIELD]: 'Time',
    [FieldTypeEnum.DATEFIELD]: 'Date',
    [FieldTypeEnum.TEXTINPUT]: 'Text input',
    [FieldTypeEnum.NUMBERINPUT]: 'Number input',
    [FieldTypeEnum.DECIMALNUMBERINPUT]: 'Decimal number input',
    [FieldTypeEnum.EMAILINPUT]: 'Email input',
    [FieldTypeEnum.PHONEINPUT]: 'Phone input',
    [FieldTypeEnum.URLINPUT]: 'URL input',
    [FieldTypeEnum.YESNO]: 'Yes/No'
  }
  const fieldTypesOptions = computed(() =>
    fieldTypes
      .map((fieldType) => ({
        value: fieldType.key,
        title: fieldTypeLabels[fieldType.key as FieldTypeEnum],
        icon: fieldTypeIcons[fieldType.key as FieldTypeEnum]
      }))
      .sort((a, b) => {
        if (!a.title) return -1
        if (!b.title) return 1
        return a.title.localeCompare(b.title)
      })
  )

  const relationshipLabels: Record<TagRelationshipEnum, string> = {
    ...Object.fromEntries(Object.entries(entitiesDE).map(([key, value]) => [key, value]))
  } as Record<TagRelationshipEnum, string>

  const relationshipOptions = computed(() =>
    Object.values(TagRelationshipEnum)
      .map((relationship) => ({
        value: relationship,
        title: relationshipLabels[relationship as TagRelationshipEnum]
      }))
      .sort((a, b) => {
        if (!a.title) return -1
        if (!b.title) return 1
        return a.title.localeCompare(b.title)
      })
  )

  const organisationsList = ref<OrganisationsList | null>(null)
  const organisations = computed(() => {
    return organisationsList.value?.results
  })

  const searchOrganizations = async (query: string): Promise<void> => {
    try {
      organisationsList.value = await OrganisationsService.organisationsList({
        limit: query ? 0 : 20, // Use all results when searching, limit to 20 when not
        search: query || undefined
      })
    } catch (error) {
      console.error('Error searching organizations:', error)
    }
  }
  const lawFields = ref<LawFields | null>(null)
  const lawFieldsOptions = computed(() => {
    const locale = localeStore.locale
    const translations = locale === 'fr' ? lawFieldsFR : locale === 'en' ? lawFieldsEN : lawFieldsDE

    return Object.keys(lawFields.value?.data || {}).map((key) => {
      return {
        value: key,
        title: translations[key] || key
      }
    })
  })
  const lawTopicOptions = computed<Option[]>(() => {
    if (!questionnaire.value || !questionnaire.value.law_field) {
      return []
    }

    const locale = localeStore.locale
    const translations = locale === 'fr' ? lawTopicsFR : {}

    return (
      lawFields.value?.data[questionnaire.value.law_field]?.map((topic) => ({
        value: topic,
        title: translations[topic] || topic
      })) || []
    ).sort((a, b) => a.title.localeCompare(b.title))
  })
  const categoryOptions = ref<Option[]>([
    {
      value: CategoryEnum.ALLGEMEIN as string,
      title: CategoryEnum.ALLGEMEIN as string
    },
    {
      value: CategoryEnum.RECHTSSPEZIFISCH as string,
      title: CategoryEnum.RECHTSSPEZIFISCH as string
    }
  ])
  const _synchronizeJumpTo = (question: QuestionnaireQuestion) => {
    /**
     * Synchronize jump_to fields to ensure that they have the same length as the choices array.
     */
    if (!question.choices) {
      question.choices = []
    }

    if (question.jump_to !== null && question.jump_to.length < question.choices.length) {
      const missingCount = question.choices.length - question.jump_to.length
      for (let i = 0; i < missingCount; i++) {
        question.jump_to.push({ value: '', uuid: null })
      }
    }
    _updateJumpTo()
  }

  const _updateJumpTo = () => {
    /**
     * Update jump_to fields to ensure that they point to existing questions.
     * If a jump_to field points to a non-existing question, it will be set to null.
     */
    questionnaire.value?.schema.forEach((section) => {
      section.items.forEach((question) => {
        if (question.jump_to) {
          question.jump_to.forEach((jumpTo) => {
            if (jumpTo.uuid === null || jumpTo.uuid === 'END') {
              return
            }

            // Check if the target question exists in any section
            const targetExists = questionnaire.value?.schema.some((s) =>
              s.items.some((q) => q.uuid === jumpTo.uuid)
            )

            if (!targetExists) {
              jumpTo.uuid = null
            }
          })
        }
      })
    })
  }

  const copyQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const newQuestion = JSON.parse(JSON.stringify(question))
    newQuestion.uuid = uuidv4()
    newQuestion.label += ' (copy)'
    const index = section.items.indexOf(question)
    section.items.splice(index + 1, 0, newQuestion)
    _synchronizeJumpTo(newQuestion)
    return newQuestion
  }

  const removeQuestion = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const index = section.items.indexOf(question)
    section.items.splice(index, 1)
    _updateJumpTo()
  }

  const getUniqueQuestionLabel = (baseLabel: string = 'New question'): string => {
    if (!questionnaire.value) return baseLabel

    // Get all existing question labels across all sections
    const existingLabels = new Set<string>()
    questionnaire.value.schema.forEach((section) => {
      section.items.forEach((question) => {
        existingLabels.add(question.label.toLowerCase())
      })
    })

    // Find the next available number
    let counter = 1
    let label = `${baseLabel} ${counter}`
    while (existingLabels.has(label.toLowerCase())) {
      counter++
      label = `${baseLabel} ${counter}`
    }

    return label
  }

  const addQuestion = (
    section: QuestionnaireSection,
    index?: number,
    baseLabel: string = 'New question'
  ) => {
    const question: QuestionnaireQuestion = {
      uuid: uuidv4(),
      label: getUniqueQuestionLabel(baseLabel),
      help: '',
      tag: '',
      tag_relationship: null,
      tag_number: null,
      type: FieldTypeEnum.TEXTINPUT,
      optional: false,
      choices: [],
      value: '',
      jump_to: null,
      starts_ai_analysis: false
    }
    section.items.splice(index === undefined ? section.items.length : index, 0, question)
  }

  const removeSection = (section: QuestionnaireSection) => {
    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index > -1) {
      questionnaire.value?.schema.splice(index, 1)
    }
    _updateJumpTo()
  }

  const getUniqueSectionTitle = (baseTitle: string): string => {
    if (!questionnaire.value) return `${baseTitle} [1]`

    // Strip trailing " [number]" suffix to get the clean base title
    const cleanBaseTitle = baseTitle.replace(/\s*\[\d+\]$/, '')

    const existingTitles = new Set<string>(
      questionnaire.value.schema.map((s) => s.title.toLowerCase())
    )

    // Find the next available number
    let counter = 1
    let newTitle = `${cleanBaseTitle} [${counter}]`
    while (existingTitles.has(newTitle.toLowerCase())) {
      counter++
      newTitle = `${cleanBaseTitle} [${counter}]`
    }

    return newTitle
  }

  const duplicateSection = (section: QuestionnaireSection) => {
    const newSection = JSON.parse(JSON.stringify(section))
    newSection.uuid = uuidv4()
    newSection.title = getUniqueSectionTitle(section.title)

    // Generate new UUIDs for all questions in the section
    newSection.items.forEach((question: QuestionnaireQuestion) => {
      question.uuid = uuidv4()
    })

    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index > -1) {
      questionnaire.value?.schema.splice(index + 1, 0, newSection)
    }

    // Update jump_to references in the questionnaire
    _updateJumpTo()

    return newSection
  }

  const addSection = (index?: number, baseTitle: string = 'New section') => {
    const section: QuestionnaireSection = {
      uuid: uuidv4(),
      title: `${baseTitle} ${questionnaire.value!.schema.length + 1}`,
      type: 'law_specific',
      items: []
    }
    questionnaire.value?.schema.splice(
      index === undefined ? questionnaire.value?.schema.length : index,
      0,
      section
    )

    // Auto-select the new section
    selectSection(section.uuid)

    return section
  }

  const moveQuestionUp = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const index = section.items.indexOf(question)
    if (index > 0) {
      section.items.splice(index, 1)
      section.items.splice(index - 1, 0, question)
    }
  }

  const moveQuestionDown = (section: QuestionnaireSection, question: QuestionnaireQuestion) => {
    const index = section.items.indexOf(question)
    if (index < section.items.length - 1) {
      section.items.splice(index, 1)
      section.items.splice(index + 1, 0, question)
    }
  }

  const moveSectionUp = (section: QuestionnaireSection) => {
    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index > 0) {
      questionnaire.value?.schema.splice(index, 1)
      questionnaire.value?.schema.splice(index - 1, 0, section)
    }
  }

  const moveSectionDown = (section: QuestionnaireSection) => {
    const index = questionnaire.value?.schema.indexOf(section)
    if (index !== undefined && index < questionnaire.value!.schema.length - 1) {
      questionnaire.value?.schema.splice(index, 1)
      questionnaire.value?.schema.splice(index + 1, 0, section)
    }
  }

  const moveSectionToPosition = (section: QuestionnaireSection, targetPosition: number) => {
    if (!questionnaire.value?.schema) return

    const currentIndex = questionnaire.value.schema.indexOf(section)
    if (currentIndex === -1 || currentIndex === targetPosition) return

    // Remove from current position
    questionnaire.value.schema.splice(currentIndex, 1)
    // Insert at target position
    questionnaire.value.schema.splice(targetPosition, 0, section)
  }

  const moveQuestionToSection = (
    fromSection: QuestionnaireSection,
    toSection: QuestionnaireSection,
    question: QuestionnaireQuestion
  ) => {
    const index = fromSection.items.indexOf(question)
    if (index > -1) {
      fromSection.items.splice(index, 1)
      toSection.items.push(question)
      _updateJumpTo()
    }
  }

  // Add selected question state for navigation
  const selectedQuestion = ref<string | null>(null)
  const selectedItem = ref<string>('questionnaire-details')

  const selectQuestion = (questionUuid: string) => {
    selectedQuestion.value = questionUuid
    selectedItem.value = questionUuid
  }

  const selectSection = (sectionUuid: string) => {
    selectedItem.value = sectionUuid
  }

  const selectQuestionnaireDetails = () => {
    selectedItem.value = 'questionnaire-details'
  }

  const selectCustomers = () => {
    selectedItem.value = 'customers'
  }

  const selectVirtualQuestion = (sectionUuid: string) => {
    selectedItem.value = `virtual-question-${sectionUuid}`
  }

  const createNewQuestionnaire = (title: string = 'New questionnaire') => {
    // Create a default empty section
    const defaultSection = {
      uuid: uuidv4(),
      title: 'Section 1',
      type: 'law_specific' as const,
      items: []
    }

    const newQuestionnaire: Questionnaire = {
      uuid: '', // Will be assigned when saved
      title: title,
      description: '',
      org: null,
      is_jupus_template: false,
      schema: [defaultSection],
      law_field: '',
      law_topic: '',
      category: undefined,
      theme_color: '',
      created_by_name: '',
      questionnaire_creation_token: '',
      org_questionnaire_creation_token: ''
    }

    questionnaire.value = newQuestionnaire
    selectedItem.value = 'questionnaire-details' // Auto-select details
  }

  const updateFromAiResponse = (response: Partial<Questionnaire>) => {
    if (!questionnaire.value) return

    questionnaire.value = {
      ...questionnaire.value,
      title: response.title || questionnaire.value.title,
      description: response.description || questionnaire.value.description,
      category: response.category || questionnaire.value.category,
      law_field: response.law_field || questionnaire.value.law_field,
      law_topic: response.law_topic || questionnaire.value.law_topic,
      schema: response.schema || questionnaire.value.schema,
      prompt: response.prompt ?? false,
      file: response.file ?? false
    }
  }

  const init = async (uuid: string) => {
    if (uuid === 'new') {
      createNewQuestionnaire()
    } else {
      questionnaire.value = (await QuestionnairesService.questionnairesRetrieve({
        uuid: uuid
      })) as Questionnaire

      // Ensure all questions have properly synchronized jump_to arrays
      questionnaire.value.schema.forEach((section) => {
        section.items.forEach((question) => {
          _synchronizeJumpTo(question)
        })
      })
    }

    // Load required data for all cases
    // GRO-103: Filter out "Amtsgericht" tag as it incorrectly requires participant section
    const HIDDEN_TAG_KEYS = ['entities.field.company_register_court']
    const allTags = (await CasesService.casesGenericDatamappersList())
      .results as GenericDataMapper[]
    tags.value = allTags.filter((tag) => !HIDDEN_TAG_KEYS.includes(tag.key))
    organisationsList.value = await OrganisationsService.organisationsList({ limit: 20 }) // Use default pagination
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  }

  return {
    questionnaire,
    organisations,
    searchOrganizations,
    lawFieldsOptions,
    lawTopicOptions,
    categoryOptions,
    tags,
    tagOptions,
    fieldTypes,
    fieldTypesOptions,
    init,
    createNewQuestionnaire,
    updateFromAiResponse,
    copyQuestion,
    removeQuestion,
    addQuestion,
    removeSection,
    duplicateSection,
    addSection,
    moveQuestionUp,
    moveQuestionDown,
    moveSectionUp,
    moveSectionDown,
    moveSectionToPosition,
    moveQuestionToSection,
    selectedQuestion,
    selectedItem,
    selectQuestion,
    selectSection,
    selectQuestionnaireDetails,
    selectCustomers,
    selectVirtualQuestion,
    relationshipOptions
  }
})
